'use client';

import YouTubeVideoPlayer from '@/components/molecules/YouTubeVideoPlayer';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@/components/ui/carousel';
import type { YouTubeVideo } from '@/types/custom';

interface YouTubeVideoSliderProps {
    videos: YouTubeVideo[];
}

export default function YouTubeVideoSlider({ videos }: YouTubeVideoSliderProps) {
    if (!videos || videos.length === 0) {
        return null;
    }

    const isSingleVideo = videos.length === 1;

    return (
        <section className="w-full bg-haug-choco-100 py-12" aria-label="YouTube Videos">
            <div className="mx-auto max-w-screen-2xl overflow-hidden px-4 md:overflow-visible">
                {isSingleVideo ? (
                    // Single video layout - centered and larger
                    <div className="flex justify-center">
                        <div className="w-full max-w-md bg-haug-ivory-100 p-2 sm:max-w-lg md:max-w-xl lg:max-w-2xl">
                            {videos.map((video) => {
                                if (!video.id) {
                                    return null;
                                }

                                return (
                                    <YouTubeVideoPlayer
                                        key={video.id}
                                        videoUrl={`https://www.youtube.com/watch?v=${video.id}`}
                                        title={video.title}
                                        showTitle={true}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    // Multiple videos layout - carousel
                    <Carousel
                        opts={{
                            align: 'start',
                            loop: false,
                            dragFree: true,
                            containScroll: 'trimSnaps',
                        }}
                        className="w-full"
                    >
                        <CarouselContent className="-ml-2 md:-ml-4">
                            {videos.map((video) => {
                                if (!video.id) {
                                    return null;
                                }

                                return (
                                    <CarouselItem
                                        key={video.id}
                                        className="basis-[85%] pl-2 sm:basis-[60%] md:basis-[45%] md:pl-4 lg:basis-[35%] xl:basis-[30%]"
                                    >
                                        <div className="relative h-full bg-haug-ivory-100 p-2">
                                            <YouTubeVideoPlayer
                                                videoUrl={`https://www.youtube.com/watch?v=${video.id}`}
                                                title={video.title}
                                                showTitle={true}
                                            />
                                        </div>
                                    </CarouselItem>
                                );
                            })}
                        </CarouselContent>
                        <CarouselPrevious
                            size="lg"
                            className="-left-6 hidden h-12 w-12 items-center justify-center border-haug-lila-200 bg-haug-lila-100 px-3.5 text-white hover:bg-haug-lila-200 md:flex"
                        />
                        <CarouselNext
                            size="lg"
                            className="-right-6 hidden h-12 w-12 items-center justify-center border-haug-lila-200 bg-haug-lila-100 px-3.5 text-white hover:bg-haug-lila-200 md:flex"
                        />
                    </Carousel>
                )}
            </div>
        </section>
    );
}
